/*
 * Copyright (C) 2026 Motorola Solutions, Inc.
 * All Rights Reserved.
 * Motorola Solutions Confidential Restricted.
 */

import React from 'react';
import Icon from '@msi/cobalt-react/icon';
import { PrimeTableColumn, SortMeta } from './PrimeTable.types';
import { useDarkTheme } from '../../../contexts/DarkThemeContext';

export interface PrimeTableHeaderProps {
  sortedColumnsWithIndices: { col: PrimeTableColumn; originalIndex: number }[];
  tableWidth: number;
  rowExpansionTemplate?: boolean;
  selectionMode?: 'single' | 'multiple' | 'checkbox';
  isAllSelected: () => boolean;
  isSomeSelected: () => boolean;
  handleSelectAll: (event: React.ChangeEvent<HTMLInputElement>) => void;
  frozenColumns: Set<string>;
  frozenPositions: { [key: string]: number };
  getColumnWidth: (field: string, defaultWidth?: string, minWidth?: string) => string;
  handleSort: (field: string) => void;
  renderSortIcon: (field: string) => React.ReactNode;
  handleColumnPin: (field: string) => void;
  handleResizeStart: (e: React.MouseEvent, field: string, currentWidth: string) => void;
}

export const PrimeTableHeader: React.FC<PrimeTableHeaderProps> = ({
  sortedColumnsWithIndices,
  tableWidth,
  rowExpansionTemplate,
  selectionMode,
  isAllSelected,
  isSomeSelected,
  handleSelectAll,
  frozenColumns,
  frozenPositions,
  getColumnWidth,
  handleSort,
  renderSortIcon,
  handleColumnPin,
  handleResizeStart,
}) => {
  const { isDarkTheme } = useDarkTheme();
  const pinnedIconColor = isDarkTheme ? 'white' : 'black';
  const unpinnedIconColor = isDarkTheme ? '#aaa' : 'grey';

  return (
    <thead>
      <tr style={{ minWidth: `${tableWidth}px` }}>
        {rowExpansionTemplate && (
          <th className="frozen-column frozen-left" style={{ width: '3rem', left: 0 }}></th>
        )}
        {selectionMode === 'checkbox' && (
          <th
            className="frozen-column frozen-left"
            style={{
              width: '3rem',
              left: rowExpansionTemplate ? '48px' : 0,
            }}
          >
            <input
              type="checkbox"
              className="selection-checkbox"
              checked={isAllSelected()}
              ref={(input) => {
                if (input) {
                  input.indeterminate = isSomeSelected();
                }
              }}
              onChange={handleSelectAll}
              title="Select all"
            />
          </th>
        )}
        {sortedColumnsWithIndices.map(({ col: column, originalIndex }, displayIndex) => {
          const fieldKey = column.field as string;
          const isFrozen = frozenColumns.has(fieldKey);
          const leftPosition = frozenPositions[fieldKey];
          const columnWidth = getColumnWidth(fieldKey, column.width, column.minWidth);

          return (
            <th
              key={fieldKey}
              className={`${column.sortable ? 'th-sortable' : ''} ${column.className || ''} ${
                column.align ? `align-${column.align}` : ''
              } ${isFrozen ? 'frozen-column frozen-left' : ''}`}
              style={{
                width: columnWidth,
                minWidth: columnWidth,
                maxWidth: columnWidth,
                ...(isFrozen ? { left: `${leftPosition}px` } : {}),
              }}
            >
              <div className="header-content">
                <span
                  className="column-title-wrapper"
                  onClick={() => column.sortable && handleSort(fieldKey)}
                  style={{ cursor: column.sortable ? 'pointer' : 'default' }}
                >
                  <span className="column-title">
                    {column.headerTemplate ? column.headerTemplate(column) : column.header}
                  </span>
                  {column.sortable && renderSortIcon(fieldKey)}
                </span>
                {fieldKey !== 'settings' && (
                  <span
                    className={`pin-icon ${isFrozen ? 'pinned' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleColumnPin(fieldKey);
                    }}
                    title={isFrozen ? 'Unpin column' : 'Pin column'}
                  >
                    {isFrozen === true ? (
                      <Icon name="ic_pin" size={14} color={pinnedIconColor} />
                    ) : (
                      <Icon name="ic_pin" size={14} color={unpinnedIconColor} />
                    )}
                  </span>
                )}
              </div>
              <div
                className="column-resize-handle"
                onMouseDown={(e) => handleResizeStart(e, fieldKey, columnWidth)}
                title="Drag to resize"
              />
            </th>
          );
        })}
      </tr>
    </thead>
  );
};

export default PrimeTableHeader;
